import React, { useEffect, useRef, useState } from 'react';

type ListItem = string;

interface PointBoost {
  name: string;
  value: number;
}

interface PointEarner {
  action: string;
  points: number;
}

interface DuelDay {
  day: number;
  theme: string;
  boosts: PointBoost[];
  pointEarners: PointEarner[];
  notes?: string;
}

const participation: ListItem[] = [
  'Commanders with Base Level 10+ can participate in Alliance Duel.',
  'Alliances with 20+ members are eligible to enter Alliance Duel.',
  'Top 32 alliances in each Warzone qualify for next-week matchmaking.',
];

const howToPlay: ListItem[] = [
  'Week-long duel vs an enemy alliance. Each day has a duel theme; earn Duel Points by completing theme tasks. Highest Duel Points wins the day; highest weekly score wins the duel.',
  'Rewards include daily Individual Points ranking (all members of both alliances), daily winning alliance rewards, and weekly winning alliance rewards.',
  'Changing alliances during the duel blocks Alliance Points for 1 day (Individual Points remain and chests can still be opened).',
  'Only packs that contain Diamonds can earn you points.',
];

const duelDays: DuelDay[] = [
  {
    day: 1,
    theme: 'Radar Training',
    boosts: [
      { name: 'All Points (non-purchased)', value: 100 },
      { name: 'Radar Completion Points', value: 35 },
    ],
    pointEarners: [
      { action: 'Use 1 Stamina', points: 300.0 },
      { action: 'Complete 1 Radar Task', points: 23500.0 },
      { action: 'Use at least 660 Hero EXP Points at a time', points: 2.0 },
      { action: 'Use 1 Drone Battle Data point', points: 6.0 },
      { action: 'Use 1 Drone Part', points: 5000.0 },
      { action: 'Buy Packs containing Diamonds (1 Diamond)', points: 30.0 },
      { action: 'Gather 100 Food (Radar gathering tasks won\'t count towards this score)', points: 40.0 },
      { action: 'Gather 100 Iron (Radar gathering tasks won\'t count towards this score)', points: 40.0 },
      { action: 'Gather 60 Coins (Radar gathering tasks won\'t count towards this score)', points: 40.0 },
    ],
    notes: 'Radar gathering tasks do not count toward the listed gather scores.',
  },
  {
    day: 2,
    theme: 'Base Building',
    boosts: [
      { name: 'All Points (non-purchased)', value: 100 },
      { name: 'Building Power Points', value: 5 },
      { name: 'Points for Speed-ups Used', value: 35 },
    ],
    pointEarners: [
      { action: 'Use 1-min Construction Speed-up', points: 117.5 },
      { action: 'Increase Building Power by 1pt', points: 20.5 },
      { action: 'Dispatch UR Trade Truck 1 time', points: 200000.0 },
      { action: 'Perform 1 UR Secret Task', points: 150000.0 },
      { action: 'Buy Packs containing Diamonds (1 Diamond)', points: 30.0 },
      { action: 'Recruit Survivor 1 time', points: 3000.0 },
    ],
  },
  {
    day: 3,
    theme: 'Tech Research',
    boosts: [
      { name: 'All Points (non-purchased)', value: 100 },
      { name: 'Tech Power Points', value: 5 },
      { name: 'Points for Speed-ups Used', value: 35 },
      { name: 'Radar Completion Points', value: 35 },
    ],
    pointEarners: [
      { action: 'Increase Tech Power by 1pt', points: 20.5 },
      { action: 'Use 1 Valor Badge', points: 600.0 },
      { action: 'Complete 1 Radar Task', points: 23500.0 },
      { action: 'Buy Packs containing Diamonds (1 Diamond)', points: 30.0 },
      { action: 'For each Lv.1 Drone Component Chest opened', points: 2200.0 },
      { action: 'For each Lv.2 Drone Component Chest opened', points: 6600.0 },
      { action: 'For each Lv.3 Drone Component Chest opened', points: 20000.0 },
      { action: 'For each Lv.4 Drone Component Chest opened', points: 60000.0 },
      { action: 'For each Lv.5 Drone Component Chest opened', points: 180000.0 },
      { action: 'For each Lv.6 Drone Component Chest opened', points: 540000.0 },
      { action: 'For each Lv.7 Drone Component Chest opened', points: 1620000.0 },
    ],
  },
  {
    day: 4,
    theme: 'Hero Advancement',
    boosts: [
      { name: 'All Points (non-purchased)', value: 100 },
      { name: 'Hero Recruitment Points', value: 35 },
    ],
    pointEarners: [
      { action: 'Perform 1 Hero Recruitment', points: 3525.0 },
      { action: 'Use at least 660 Hero EXP Points at a time', points: 2.0 },
      { action: 'Use 1 UR Hero Shard', points: 20000.0 },
      { action: 'Use 1 SSR Hero Shard', points: 7000.0 },
      { action: 'Use 1 SR Hero Shard', points: 2000.0 },
      { action: 'Use 1 Skill Medal', points: 20.0 },
      { action: 'Buy Packs containing Diamonds (1 Diamond)', points: 30.0 },
    ],
  },
  {
    day: 5,
    theme: 'Unit Training',
    boosts: [
      { name: 'All Points (non-purchased)', value: 100 },
      { name: 'Radar Completion Points', value: 35 },
      { name: 'Building Power Points', value: 5 },
      { name: 'Tech Power Points', value: 5 },
      { name: 'Troop Training Points', value: 30 },
      { name: 'Points for Speed-ups Used', value: 35 },
    ],
    pointEarners: [
      { action: 'Use 1-min Construction Speed-up', points: 117.5 },
      { action: 'Increase Building Power by 1pt', points: 20.5 },
      { action: 'Use 1-min Research Speed-up', points: 117.5 },
      { action: 'Increase Tech Power by 1pt', points: 20.5 },
      { action: 'Use 1-min Training Speed-up', points: 117.5 },
      { action: 'Train a Lv.1 unit', points: 46.0 },
      { action: 'Train a Lv.2 unit', points: 69.0 },
      { action: 'Train a Lv.3 unit', points: 92.0 },
      { action: 'Train a Lv.4 unit', points: 115.0 },
      { action: 'Train a Lv.5 unit', points: 138.0 },
      { action: 'Train a Lv.6 unit', points: 161.0 },
      { action: 'Train a Lv.7 unit', points: 184.0 },
      { action: 'Train a Lv.8 unit', points: 207.0 },
      { action: 'Train a Lv.9 unit', points: 230.0 },
      { action: 'Train a Lv.10 unit', points: 253.0 },
      { action: 'Buy Packs containing Diamonds (1 Diamond)', points: 30.0 },
    ],
  },
  {
    day: 6,
    theme: 'Combat',
    boosts: [
      { name: 'All Points (non-purchased)', value: 100 },
      { name: 'Points for Speed-ups Used', value: 35 },
      { name: 'Kill Points', value: 30 },
    ],
    pointEarners: [
      { action: 'Dispatch UR Trade Truck 1 time', points: 200000.0 },
      { action: 'Perform 1 UR Secret Task', points: 150000.0 },
      { action: 'Use 1-min Construction Speed-up', points: 117.5 },
      { action: 'Use 1-min Research Speed-up', points: 117.5 },
      { action: 'Use 1-min Training Speed-up', points: 117.5 },
      { action: 'Use 1-min Healing Speed-up', points: 117.5 },
      { action: 'Every Lv.1 rival Alliance unit killed', points: 23.0 },
      { action: 'Every Lv.2 rival Alliance unit killed', points: 34.5 },
      { action: 'Every Lv.3 rival Alliance unit killed', points: 46.0 },
      { action: 'Every Lv.4 rival Alliance unit killed', points: 57.5 },
      { action: 'Every Lv.5 rival Alliance unit killed', points: 69.0 },
      { action: 'Every Lv.6 rival Alliance unit killed', points: 80.5 },
      { action: 'Every Lv.7 rival Alliance unit killed', points: 92.0 },
      { action: 'Every Lv.8 rival Alliance unit killed', points: 103.5 },
      { action: 'Every Lv.9 rival Alliance unit killed', points: 115.0 },
      { action: 'Every Lv.10 rival Alliance unit killed', points: 126.5 },
      { action: 'Every Lv.1 unit lost', points: 4.0 },
      { action: 'Every Lv.2 unit lost', points: 6.0 },
      { action: 'Every Lv.3 unit lost', points: 8.0 },
      { action: 'Every Lv.4 unit lost', points: 10.0 },
      { action: 'Every Lv.5 unit lost', points: 12.0 },
      { action: 'Every Lv.6 unit lost', points: 14.0 },
      { action: 'Every Lv.7 unit lost', points: 16.0 },
      { action: 'Every Lv.8 unit lost', points: 18.0 },
      { action: 'Every Lv.9 unit lost', points: 20.0 },
      { action: 'Every Lv.10 unit lost', points: 22.0 },
      { action: 'Buy Packs containing Diamonds (1 Diamond)', points: 30.0 },
    ],
  },
  {
    day: 7,
    theme: 'Mixed Activities',
    boosts: [
      { name: 'All Points (non-purchased)', value: 100 },
      { name: 'Points for Speed-ups Used', value: 35 },
      { name: 'Kill Points', value: 30 },
    ],
    pointEarners: [
      { action: 'Every Lv.1 rival Alliance unit killed', points: 4.6 },
      { action: 'Every Lv.2 rival Alliance unit killed', points: 6.9 },
      { action: 'Every Lv.3 rival Alliance unit killed', points: 9.2 },
      { action: 'Every Lv.4 rival Alliance unit killed', points: 11.5 },
      { action: 'Every Lv.5 rival Alliance unit killed', points: 13.8 },
      { action: 'Every Lv.6 rival Alliance unit killed', points: 16.1 },
      { action: 'Every Lv.7 rival Alliance unit killed', points: 18.4 },
      { action: 'Every Lv.8 rival Alliance unit killed', points: 20.7 },
      { action: 'Every Lv.9 rival Alliance unit killed', points: 23.0 },
      { action: 'Every Lv.10 rival Alliance unit killed', points: 25.3 },
      { action: 'Every Lv.1 unit lost', points: 4.0 },
      { action: 'Every Lv.2 unit lost', points: 6.0 },
      { action: 'Every Lv.3 unit lost', points: 8.0 },
      { action: 'Every Lv.4 unit lost', points: 10.0 },
      { action: 'Every Lv.5 unit lost', points: 12.0 },
      { action: 'Every Lv.6 unit lost', points: 14.0 },
      { action: 'Every Lv.7 unit lost', points: 16.0 },
      { action: 'Every Lv.8 unit lost', points: 18.0 },
      { action: 'Every Lv.9 unit lost', points: 20.0 },
      { action: 'Every Lv.10 unit lost', points: 22.0 },
      { action: 'Buy Packs containing Diamonds (1 Diamond)', points: 30.0 },
    ],
  },
];

const AllianceDuel: React.FC = () => {
  const [selectedDay, setSelectedDay] = useState<number>(1);
  const cardsRef = useRef<HTMLDivElement[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -40px 0px' }
    );

    cardsRef.current.forEach((card) => {
      if (card) {
        card.classList.add('fade-in-ready');
        observer.observe(card);
      }
    });

    return () => {
      cardsRef.current.forEach((card) => {
        if (card) observer.unobserve(card);
      });
    };
  }, []);

  const currentDay = duelDays.find((d) => d.day === selectedDay) || duelDays[0];

  return (
    <section id="alliance-duel" className="section section-dark">
      <div className="container">
        <h2 className="section-title">Alliance Duel</h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div
            className="bg-bg-card border border-border rounded-xl p-6 shadow-glow"
            ref={(el) => { if (el) cardsRef.current[0] = el; }}
          >
            <h3 className="font-orbitron text-xl text-primary-light mb-3">Participation Conditions</h3>
            <ul className="list-none space-y-3 text-text-secondary">
              {participation.map((item, idx) => (
                <li key={idx} className="flex gap-2 items-start">
                  <span className="text-primary">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            className="bg-bg-card border border-border rounded-xl p-6"
            ref={(el) => { if (el) cardsRef.current[1] = el; }}
          >
            <h3 className="font-orbitron text-xl text-primary-light mb-3">How To Play</h3>
            <ul className="list-none space-y-3 text-text-secondary">
              {howToPlay.map((item, idx) => (
                <li key={idx} className="flex gap-2 items-start">
                  <span className="text-primary">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div
          className="bg-bg-card border border-border rounded-xl p-6"
          ref={(el) => { if (el) cardsRef.current[2] = el; }}
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h3 className="font-orbitron text-xl text-primary-light">Duel Themes</h3>
              <p className="text-text-secondary">Select a day to view scoring rules and point boosts.</p>
            </div>
            <span className="text-sm text-text-muted uppercase tracking-wide">Server Time</span>
          </div>

          {/* Day Tabs */}
          <div className="flex flex-wrap gap-2 mb-6 border-b border-border pb-4">
            {duelDays.map((day) => (
              <button
                key={day.day}
                onClick={() => setSelectedDay(day.day)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedDay === day.day
                    ? 'bg-primary text-bg-dark border border-primary shadow-glow'
                    : 'bg-bg-darker text-text-secondary border border-border hover:border-primary/50 hover:text-primary-light'
                }`}
              >
                Day {day.day}
              </button>
            ))}
          </div>

          {/* Selected Day Content */}
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <h4 className="font-orbitron text-2xl text-primary-light">
                Day {currentDay.day}: {currentDay.theme}
              </h4>
            </div>

            {/* Points Boost Section */}
            {currentDay.boosts.length > 0 && (
              <div className="mb-6 p-4 bg-bg-darker border border-border rounded-lg">
                <h5 className="font-orbitron text-lg text-primary-light mb-3">Points Boost</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {currentDay.boosts.map((boost, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-bg-card rounded border border-border">
                      <span className="text-text-primary">{boost.name}</span>
                      <span className="font-orbitron text-primary-light font-semibold">+{boost.value}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Point Earners Table */}
            <div className="overflow-hidden rounded-lg border border-border">
              <div className="grid grid-cols-3 bg-bg-darker text-text-muted text-sm font-semibold uppercase tracking-wide">
                <div className="px-4 py-3">Action</div>
                <div className="px-4 py-3 text-center col-span-2">Points</div>
              </div>
              <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
                {currentDay.pointEarners.map((earner, idx) => (
                  <div
                    key={idx}
                    className="grid grid-cols-3 items-center px-4 py-3 bg-bg-card hover:bg-bg-darker transition-colors"
                  >
                    <div className="text-text-primary text-sm">{earner.action}</div>
                    <div className="col-span-2 text-right font-orbitron text-lg text-primary-light">
                      +{earner.points.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {currentDay.notes && (
              <p className="text-text-muted text-sm mt-3">{currentDay.notes}</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AllianceDuel;
